import {
    FunctionComponent,
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import { Navbar } from 'src/shared';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useAppDispatch from 'src/hooks/useAppDispatch';
import { ClientSelectors } from 'src/redux/client/selectors';
import { PlanningSelectors } from 'src/redux/planning/selectors';
import { getProject } from 'src/redux/projects/selectors';
import { RoutesEnum } from 'src/types/navigation';
import { PlanningSessionStatus } from 'src/types/planning';
import {
    startPlanningSession,
    fetchPlanningSession,
    submitAnswers,
    submitFeedback,
    editPlan,
    finalizePlan,
    offloadPlanningSession,
    retryPlanningSession
} from 'src/redux/planning/service';
import { createDraftQuest } from 'src/redux/quests/service';
import { resetPlanning } from 'src/redux/planning/slice';
import { showSnackbar } from 'src/utils/snackbar';
import { checkAllLinksAccessibility } from 'src/utils/linkAccessibility';
import PlanningInput from './components/PlanningInput';
import Spinner from 'src/shared/Spinner';
import PlanningQuestions from './components/PlanningQuestions';
import PlanReview from './components/PlanReview';
import FinalizeQuestModal from './components/FinalizeQuestModal';
import ClarityBadge from './components/ClarityBadge';
import PhaseBadge from './components/PhaseBadge';
import TechnicalFork from './components/TechnicalFork';

import {
    PageRoot,
    ContentBox,
    ErrorBox,
    errorText,
    successText,
    RetryButton,
    ViewQuestButton,
    OutlinedActionButton
} from './styled';
import { Typography } from '@mui/material';
import { QuestStatus } from 'src/enums/quests';
import { SubmitAnswerItem } from 'src/types/planning';
import { getQuest as getQuestApi, deleteDraftQuest } from 'src/api/quests';

const POLL_INTERVAL = 3000;

const ACTIVE_STATUSES = new Set([
    PlanningSessionStatus.AWAITING_ANSWERS,
    PlanningSessionStatus.PLAN_READY
]);

const PlanningSession: FunctionComponent = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { sessionId: urlSessionId } = useParams<{ sessionId?: string }>();
    const { selectedProjectId } = ClientSelectors();
    const draftQuestId = searchParams.get('questId') ?? undefined;
    const [draftDescription, setDraftDescription] = useState<
        string | undefined
    >(undefined);
    const project = useSelector(getProject(selectedProjectId));

    const {
        sessionId,
        status,
        questions,
        plan,
        questId,
        clarityScore,
        isLoading,
        isOffloading,
        error
    } = PlanningSelectors();

    const pollRef = useRef<NodeJS.Timeout | null>(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [showFinalizeModal, setShowFinalizeModal] = useState(false);
    const [showOffloadModal, setShowOffloadModal] = useState(false);
    // Tracks whether the delegator dismissed the technical-fork screen
    // for the current batch. Reset whenever the question set changes
    // (new batch arrives) so the fork appears again if the next batch
    // is also technical.
    const [acceptedTechnical, setAcceptedTechnical] = useState(false);
    const questionsKey = questions
        ? questions.map((q) => q.question).join('|')
        : '';
    useEffect(() => {
        setAcceptedTechnical(false);
    }, [questionsKey]);

    const currentPhase: 'product' | 'technical' | null =
        questions && questions.length > 0
            ? (questions[0].questionPhase as 'product' | 'technical' | null)
            : null;
    const showTechnicalFork =
        !!questions &&
        questions.length > 0 &&
        currentPhase === 'technical' &&
        !acceptedTechnical;

    const handleSendToBeehive = useCallback(() => {
        setShowOffloadModal(true);
    }, []);

    const handleOffloadConfirm = useCallback(
        ({
            priority,
            status: questStatus,
            alwaysHumanReview
        }: {
            priority: number;
            status: number;
            alwaysHumanReview: boolean;
        }) => {
            if (!sessionId) return;
            dispatch(
                offloadPlanningSession({
                    sessionId,
                    params: {
                        priority,
                        status: questStatus,
                        alwaysHumanReview
                    }
                })
            )
                .unwrap()
                .then(() => {
                    setShowOffloadModal(false);
                    navigate(RoutesEnum.CLIENT_DASHBOARD, { replace: true });
                })
                .catch(() =>
                    showSnackbar(
                        dispatch,
                        'error',
                        'Failed to hand the planning over to Beehive'
                    )
                );
        },
        [dispatch, sessionId, navigate]
    );
    const [quickDelegateData, setQuickDelegateData] = useState<{
        description: string;
        attachmentList?: number[];
    } | null>(null);
    const [isCheckingLinks, setIsCheckingLinks] = useState(false);

    // Verifies that any Figma/Google Docs/Drive links in the description are
    // publicly accessible. Returns true when it's safe to proceed. Shows an
    // error snackbar and returns false otherwise.
    const ensureLinksAccessible = useCallback(
        async (description: string): Promise<boolean> => {
            setIsCheckingLinks(true);
            try {
                const { allAccessible, inaccessibleLinks } =
                    await checkAllLinksAccessibility(description);
                if (!allAccessible) {
                    showSnackbar(
                        dispatch,
                        'error',
                        `The following links are not public: ${inaccessibleLinks.join(
                            ', '
                        )}`
                    );
                    return false;
                }
                return true;
            } catch {
                showSnackbar(
                    dispatch,
                    'error',
                    'Failed to verify that links are public'
                );
                return false;
            } finally {
                setIsCheckingLinks(false);
            }
        },
        [dispatch]
    );
    useEffect(() => {
        if (urlSessionId && !sessionId) {
            dispatch(fetchPlanningSession(urlSessionId));
        } else if (!urlSessionId) {
            dispatch(resetPlanning());
        }
    }, [urlSessionId, sessionId, dispatch]);

    useEffect(() => {
        if (draftQuestId) {
            getQuestApi(draftQuestId)
                .then((res) => {
                    const description =
                        res?.data?.description ?? res?.description;
                    if (description) setDraftDescription(description);
                })
                .catch(() => {});
        }
    }, [draftQuestId]);

    useEffect(() => {
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
            dispatch(resetPlanning());
        };
    }, [dispatch]);

    useEffect(() => {
        if (sessionId && status === PlanningSessionStatus.PROCESSING) {
            pollRef.current = setInterval(() => {
                dispatch(fetchPlanningSession(sessionId));
            }, POLL_INTERVAL);
        }
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [sessionId, status, dispatch]);

    const handleStart = useCallback(
        async (description: string, attachmentList?: number[]) => {
            if (!project?.id) return;
            if (!(await ensureLinksAccessible(description))) return;
            dispatch(
                startPlanningSession({
                    projectId: project.id,
                    description,
                    attachmentList,
                    planningNeeded: true,
                    status: QuestStatus.PLANNING,
                    questId: draftQuestId
                })
            )
                .unwrap()
                .then((res) => {
                    if (res.sessionId) {
                        navigate(
                            RoutesEnum.PLAN_QUEST_SESSION.replace(
                                ':sessionId',
                                res.sessionId
                            ),
                            { replace: true }
                        );
                    }
                })
                .catch(() =>
                    showSnackbar(dispatch, 'error', 'Failed to start planning')
                );
        },
        [dispatch, project, navigate, draftQuestId, ensureLinksAccessible]
    );

    const handleQuickDelegate = useCallback(
        async (description: string, attachmentList?: number[]) => {
            if (!(await ensureLinksAccessible(description))) return;
            setQuickDelegateData({ description, attachmentList });
            setShowFinalizeModal(true);
        },
        [ensureLinksAccessible]
    );

    const handleDeleteDraft = useCallback(() => {
        if (!draftQuestId) return;
        deleteDraftQuest(draftQuestId)
            .then(() => {
                showSnackbar(dispatch, 'success', 'Draft deleted successfully');
                navigate(RoutesEnum.CLIENT_DASHBOARD);
            })
            .catch(() =>
                showSnackbar(dispatch, 'error', 'Failed to delete draft')
            );
    }, [draftQuestId, dispatch, navigate]);

    const handleSaveAsDraft = useCallback(
        (description: string, attachmentList?: number[]) => {
            if (!project?.id) return;
            dispatch(
                createDraftQuest({
                    projectId: project.id,
                    description,
                    attachmentList,
                    questId: draftQuestId
                })
            )
                .unwrap()
                .then(() => {
                    showSnackbar(
                        dispatch,
                        'success',
                        'Draft saved successfully!'
                    );
                    navigate(RoutesEnum.CLIENT_DASHBOARD);
                })
                .catch(() =>
                    showSnackbar(dispatch, 'error', 'Failed to save draft')
                );
        },
        [dispatch, project, navigate, draftQuestId]
    );

    const handleAnswers = useCallback(
        (answers: SubmitAnswerItem[]) => {
            if (!sessionId) return;
            dispatch(submitAnswers({ sessionId, params: { answers } }))
                .unwrap()
                .catch(() =>
                    showSnackbar(dispatch, 'error', 'Failed to submit answers')
                );
        },
        [dispatch, sessionId]
    );

    const handleFeedback = useCallback(
        (feedbackText: string, attachmentList?: number[]) => {
            if (!sessionId) return;
            setShowFeedback(false);
            dispatch(
                submitFeedback({
                    sessionId,
                    params: { feedback: feedbackText, attachmentList }
                })
            )
                .unwrap()
                .catch(() =>
                    showSnackbar(dispatch, 'error', 'Failed to submit feedback')
                );
        },
        [dispatch, sessionId]
    );

    const handleEditSave = useCallback(
        (planText: string, attachmentList?: number[]) => {
            if (!sessionId) return;
            dispatch(
                editPlan({
                    sessionId,
                    params: { plan: planText, attachmentList }
                })
            )
                .unwrap()
                .then(() => setIsEditMode(false))
                .catch(() =>
                    showSnackbar(dispatch, 'error', 'Failed to save edits')
                );
        },
        [dispatch, sessionId]
    );

    const canFinalize = !!plan && (clarityScore == null || clarityScore >= 2);

    const handleFinalize = useCallback(
        ({
            priority,
            status: questStatus,
            alwaysHumanReview
        }: {
            priority: number;
            status: number;
            alwaysHumanReview: boolean;
        }) => {
            if (quickDelegateData) {
                if (!project?.id) return;
                dispatch(
                    startPlanningSession({
                        projectId: project.id,
                        description: quickDelegateData.description,
                        attachmentList: quickDelegateData.attachmentList,
                        planningNeeded: false,
                        status: questStatus,
                        priority,
                        questId: draftQuestId,
                        alwaysHumanReview
                    })
                )
                    .unwrap()
                    .then((res) => {
                        setShowFinalizeModal(false);
                        setQuickDelegateData(null);
                        showSnackbar(
                            dispatch,
                            'success',
                            'Request delegated successfully!'
                        );
                        if (res.questId) {
                            navigate(
                                RoutesEnum.QUEST_DETAIL.replace(
                                    ':id',
                                    res.questId
                                ),
                                {
                                    state: {
                                        breakdownJobId: res.breakdownJobId
                                    }
                                }
                            );
                        }
                    })
                    .catch(() =>
                        showSnackbar(
                            dispatch,
                            'error',
                            'Failed to delegate request'
                        )
                    );
            } else {
                if (!sessionId) return;
                dispatch(
                    finalizePlan({
                        sessionId,
                        params: {
                            priority,
                            status: questStatus,
                            alwaysHumanReview
                        }
                    })
                )
                    .unwrap()
                    .then((res) => {
                        setShowFinalizeModal(false);
                        showSnackbar(
                            dispatch,
                            'success',
                            'Request created successfully!'
                        );
                        navigate(
                            RoutesEnum.QUEST_DETAIL.replace(':id', res.questId),
                            {
                                state: {
                                    breakdownJobId: res.breakdownJobId
                                }
                            }
                        );
                    })
                    .catch(() =>
                        showSnackbar(
                            dispatch,
                            'error',
                            'Failed to create request'
                        )
                    );
            }
        },
        [
            dispatch,
            sessionId,
            navigate,
            quickDelegateData,
            project,
            draftQuestId
        ]
    );

    const isActive = status != null && ACTIVE_STATUSES.has(status);
    const hasPlan = isActive && !!plan;
    const hasQuestions =
        status === PlanningSessionStatus.AWAITING_ANSWERS && !!questions;

    const renderContent = () => {
        if (!status) {
            return (
                <PlanningInput
                    onSubmit={handleStart}
                    onQuickDelegate={handleQuickDelegate}
                    onSaveAsDraft={handleSaveAsDraft}
                    onDeleteDraft={draftQuestId ? handleDeleteDraft : undefined}
                    isLoading={isLoading || isCheckingLinks}
                    initialDescription={draftDescription}
                />
            );
        }

        if (status === PlanningSessionStatus.PROCESSING) {
            return (
                <>
                    {plan && (
                        <PlanReview
                            plan={plan}
                            isEditMode={false}
                            showFeedback={false}
                            onEdit={() => {}}
                            onEditCancel={() => {}}
                            onEditSave={() => {}}
                            onFeedbackOpen={() => {}}
                            onFeedbackCancel={() => {}}
                            onFeedbackSubmit={() => {}}
                            onFinalize={() => {}}
                            canFinalize={false}
                            isLoading
                            readOnly
                        />
                    )}
                    <Spinner text="Analyzing, may take a few minutes" />
                </>
            );
        }

        if (hasPlan) {
            return (
                <>
                    {clarityScore != null && (
                        <ClarityBadge score={clarityScore} />
                    )}
                    {currentPhase && hasQuestions && (
                        <PhaseBadge phase={currentPhase} />
                    )}
                    <PlanReview
                        plan={plan!}
                        isEditMode={isEditMode}
                        showFeedback={showFeedback}
                        onEdit={() => setIsEditMode(true)}
                        onEditCancel={() => setIsEditMode(false)}
                        onEditSave={handleEditSave}
                        onFeedbackOpen={() => setShowFeedback(true)}
                        onFeedbackCancel={() => setShowFeedback(false)}
                        onFeedbackSubmit={handleFeedback}
                        onFinalize={() => setShowFinalizeModal(true)}
                        canFinalize={canFinalize}
                        isLoading={isLoading}
                    />
                    {hasQuestions && showTechnicalFork && (
                        <TechnicalFork
                            onSelfAnswer={() => setAcceptedTechnical(true)}
                            onOffload={handleSendToBeehive}
                            isOffloading={isOffloading}
                        />
                    )}
                    {hasQuestions && !showTechnicalFork && (
                        <>
                            <PlanningQuestions
                                questions={questions!}
                                onSubmit={handleAnswers}
                                collapsible
                            />
                            {acceptedTechnical && (
                                <TechnicalFork
                                    onSelfAnswer={() => {}}
                                    onOffload={handleSendToBeehive}
                                    isOffloading={isOffloading}
                                    hideSelfAnswer
                                />
                            )}
                        </>
                    )}
                </>
            );
        }

        if (hasQuestions && !plan) {
            return (
                <>
                    {clarityScore != null && (
                        <ClarityBadge score={clarityScore} />
                    )}
                    {currentPhase && <PhaseBadge phase={currentPhase} />}
                    {showTechnicalFork ? (
                        <TechnicalFork
                            onSelfAnswer={() => setAcceptedTechnical(true)}
                            onOffload={handleSendToBeehive}
                            isOffloading={isOffloading}
                        />
                    ) : (
                        <>
                            <PlanningQuestions
                                questions={questions!}
                                onSubmit={handleAnswers}
                            />
                            {acceptedTechnical && (
                                <TechnicalFork
                                    onSelfAnswer={() => {}}
                                    onOffload={handleSendToBeehive}
                                    isOffloading={isOffloading}
                                    hideSelfAnswer
                                />
                            )}
                        </>
                    )}
                </>
            );
        }

        if (status === PlanningSessionStatus.FINALIZED && questId) {
            return (
                <ErrorBox>
                    <Typography sx={successText}>
                        Request created successfully.
                    </Typography>
                    <ViewQuestButton
                        variant="contained"
                        onClick={() =>
                            navigate(
                                RoutesEnum.QUEST_DETAIL.replace(':id', questId)
                            )
                        }
                    >
                        View Request
                    </ViewQuestButton>
                </ErrorBox>
            );
        }

        if (status === PlanningSessionStatus.FAILED) {
            return (
                <ErrorBox>
                    <Typography sx={errorText}>
                        Something went wrong. {error || 'Please try again.'}
                    </Typography>
                    <RetryButton
                        variant="contained"
                        onClick={() => {
                            if (sessionId) {
                                dispatch(retryPlanningSession(sessionId));
                            }
                        }}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Retrying...' : 'Start Over'}
                    </RetryButton>
                </ErrorBox>
            );
        }

        if (status === PlanningSessionStatus.EXPIRED) {
            return (
                <ErrorBox>
                    <Typography sx={errorText}>
                        This planning session has expired. Please start a new
                        one.
                    </Typography>
                    <OutlinedActionButton
                        variant="contained"
                        onClick={() => dispatch(resetPlanning())}
                    >
                        Start Over
                    </OutlinedActionButton>
                </ErrorBox>
            );
        }

        return null;
    };

    return (
        <Navbar>
            <PageRoot>
                <ContentBox>{renderContent()}</ContentBox>
                <FinalizeQuestModal
                    open={showFinalizeModal}
                    onClose={() => {
                        setShowFinalizeModal(false);
                        setQuickDelegateData(null);
                    }}
                    onConfirm={handleFinalize}
                    isLoading={isLoading}
                />
                <FinalizeQuestModal
                    open={showOffloadModal}
                    onClose={() => setShowOffloadModal(false)}
                    onConfirm={handleOffloadConfirm}
                    isLoading={isOffloading}
                    title="Send to Beehive"
                    confirmLabel="Send to Beehive"
                />
            </PageRoot>
        </Navbar>
    );
};

export default PlanningSession;
